import { Type } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { CONTENT_ITEM_KIND } from './../../api';
import { isNotNil } from './../../utils';
import {
  LayoutComponentDirective,
  LayoutMappingDirective
} from './../decorators/layout/layout.directive';
import { assertNotNil, isAssertOn } from './../internal/assert';
import { lid } from './../services/logger/logger.service';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'ComponentMappingUtils';
const _ReplaySubject = ReplaySubject;

export interface RegisteredMapping {
  /*
   * Defines the mapping between the layout controller
   * to the layout implementation. If this property is missing, the selector
   * of the @Component will be used instead.
   */
  selector: string | string[] | Type<any>;

  /*
   * An optional layout mode used with this layout mapping.
   */
  layoutMode?: string | string[];

  /**
   * Type IDs or content IDs to map this to
   */
  id?: string | string[];

  /**
   * Type IDs to map this to
   */
  kind?: CONTENT_ITEM_KIND | CONTENT_ITEM_KIND[];
}

// allows to attach for modifications of the components
const _mappingsSubject = new _ReplaySubject<RegisteredMapping>();

/*
 * Returns the set of registered mappings
 * @return the observable of the mapping
 */
function _getRegistered(): Observable<RegisteredMapping> {
  return _mappingsSubject;
}

/*
 * Register a mapping from the description of a layout component
 *
 * @param aDirective     the directive
 * @param aType     the associated type
 */
function _registerFromLayoutComponent(
  aDirective: LayoutComponentDirective,
  aType: Type<any>
): void {
  // sanity check
  if (isAssertOn()) {
    assertNotNil(aType);
  }
  // register this
  lid(LOGGER, 'registerMapping', aDirective, aType.name);
  // add the component
  const mappingId = aDirective.mappingId;
  if (isNotNil(mappingId)) {
    // register
    _mappingsSubject.next({
      selector: aType,
      id: mappingId,
      layoutMode: aDirective.layoutMode
    });
  }
}

/*
 * Register a mapping from the description of a layout directive
 *
 * @param aDirective     the directive
 * @param aType     the associated type
 */
function _registerFromMappingDirective(
  aDirective: LayoutMappingDirective,
  aComponent: Type<any>
): void {
  // register this
  lid(LOGGER, 'registerMapping', aDirective);
  // add the component
  const mapping: RegisteredMapping = {
    ...aDirective,
    selector: aDirective.selector || aComponent
  };
  // register
  _mappingsSubject.next(mapping);
}

function _destroy(): void {
  // done with the sequence
  _mappingsSubject.complete();
}

export {
  _getRegistered as mappingGetRegistered,
  _registerFromLayoutComponent as mappingRegisterFromLayoutComponent,
  _registerFromMappingDirective as mappingRegisterFromMappingDirective,
  _destroy as mappingDestroy
};
